import { toLocale, type CurrentUser, type Locale } from '@recipes/contracts';
import { applyLocale, currentLocale } from './index';

/**
 * Which language wins (docs/partial/localization-spec.md 5.2):
 * - a choice made by hand is applied at once and stored on the device;
 * - signed in, it is saved to the profile – now, or when the connection is back;
 * - signing in applies the profile's language to the device, unless the visitor chose a
 *   language by hand during this visit: then that choice is written to the profile.
 */

/** sessionStorage: a language was chosen by hand during this visit (before signing in). */
const EXPLICIT_KEY = 'rm.locale.explicit';
/** localStorage: a choice that still has to reach the profile. */
const PENDING_KEY = 'rm.locale.pending';

function read(storage: 'session' | 'local', key: string): string | null {
  try {
    return (storage === 'session' ? sessionStorage : localStorage).getItem(key);
  } catch {
    return null;
  }
}
function write(storage: 'session' | 'local', key: string, value: string | null): void {
  try {
    const target = storage === 'session' ? sessionStorage : localStorage;
    if (value === null) target.removeItem(key);
    else target.setItem(key, value);
  } catch {
    // storage unavailable: the choice still applies to this page
  }
}

export function pendingLocale(): Locale | null {
  const value = read('local', PENDING_KEY);
  return value ? toLocale(value) : null;
}

/** The user picked a language. `signedIn` decides whether the profile has to follow. */
export async function chooseLocale(locale: Locale, signedIn: boolean): Promise<void> {
  await applyLocale(locale);
  if (signedIn) write('local', PENDING_KEY, locale);
  else write('session', EXPLICIT_KEY, locale);
}

export type SaveLocale = (locale: Locale) => Promise<CurrentUser['locale']>;

/**
 * Brings device and profile together. Called when a user is signed in, when their profile
 * language changes and when the connection returns. Returns the language the profile has
 * afterwards, or null when nothing had to be saved or saving has to wait.
 */
export async function reconcileLocale(
  profileLocale: Locale,
  save: SaveLocale,
  online: boolean,
): Promise<Locale | null> {
  const explicit = read('session', EXPLICIT_KEY);
  if (explicit) {
    // Chosen on the login page a moment ago: that is what the user wants from now on.
    write('session', EXPLICIT_KEY, null);
    write('local', PENDING_KEY, toLocale(explicit));
  }
  const pending = pendingLocale();
  if (!pending) {
    if (currentLocale() !== profileLocale) await applyLocale(profileLocale).catch(() => undefined);
    return null;
  }
  if (pending === profileLocale) {
    write('local', PENDING_KEY, null);
    return null;
  }
  if (!online) return null;
  try {
    const saved = await save(pending);
    // Only forget the choice that was saved – another one may have been made meanwhile.
    if (pendingLocale() === pending) write('local', PENDING_KEY, null);
    return saved;
  } catch {
    return null; // stays pending; the next sign-in, profile load or reconnect tries again
  }
}

/** Signing out keeps the device language but forgets what was meant for the old profile. */
export function forgetPendingLocale(): void {
  write('local', PENDING_KEY, null);
}
